import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Building2,
  Search,
  Filter,
  ArrowLeftRight,
  Users,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  Ban,
  Play,
  MessageSquarePlus,
  RefreshCw,
  Plus,
  Phone,
  Mail,
  Globe,
  Wallet,
  Copy,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Network logos
const MTN_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/New-mtn-logo.jpg/1200px-New-mtn-logo.jpg";
const ORANGE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Orange_logo.svg/1200px-Orange_logo.svg.png";

export const Merchants = () => {
  const { isSuperAdmin, canAddNotes } = useAdminAuth();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Create Merchant Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newMerchant, setNewMerchant] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    country: 'Liberia',
    currency_preference: 'Both',
    assigned_port: '',
    password: ''
  });
  const [createdMerchant, setCreatedMerchant] = useState(null);
  
  // Payment Sources Modal
  const [paymentSourcesOpen, setPaymentSourcesOpen] = useState(false);
  const [paymentSources, setPaymentSources] = useState([]);
  const [paymentSourcesLoading, setPaymentSourcesLoading] = useState(false);
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [newSource, setNewSource] = useState({ network: 'MTN', phone_number: '', label: '' });

  useEffect(() => {
    fetchMerchants();
  }, [statusFilter]);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const response = await axios.get(`${API}/admin/merchants?${params}`, { withCredentials: true });
      setMerchants(response.data);
    } catch (error) {
      toast.error('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantDetails = async (merchantId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/merchants/${merchantId}`, { withCredentials: true });
      setMerchantDetails(response.data);
    } catch (error) {
      toast.error('Failed to load merchant details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openDetails = (merchant) => {
    setSelectedMerchant(merchant);
    setDetailsOpen(true);
    fetchMerchantDetails(merchant.merchant_id);
  };

  const handleStatusChange = async (merchant, activate) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/admin/merchants/${merchant.merchant_id}/status`, {
        is_active: activate,
        reason: statusReason || null
      }, { withCredentials: true });
      toast.success(`Merchant ${activate ? 'activated' : 'suspended'} successfully`);
      setStatusModalOpen(false);
      setStatusReason('');
      fetchMerchants();
      if (detailsOpen) fetchMerchantDetails(merchant.merchant_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update merchant status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/merchants/${selectedMerchant.merchant_id}/notes`, {
        merchant_id: selectedMerchant.merchant_id,
        note: newNote.trim()
      }, { withCredentials: true });
      toast.success('Support note added');
      setNoteModalOpen(false);
      setNewNote('');
      fetchMerchantDetails(selectedMerchant.merchant_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMerchant = async () => {
    if (!selectedMerchant || deleteConfirmText !== selectedMerchant.business_name) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/merchants/${selectedMerchant.merchant_id}`, { withCredentials: true });
      toast.success(`Merchant "${selectedMerchant.business_name}" has been permanently deleted`);
      setDeleteModalOpen(false);
      setDeleteConfirmText('');
      setDetailsOpen(false);
      setSelectedMerchant(null);
      fetchMerchants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete merchant');
    } finally {
      setActionLoading(false);
    }
  };

  // Create new merchant
  const handleCreateMerchant = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const response = await axios.post(`${API}/admin/merchants`, newMerchant, { withCredentials: true });
      setCreatedMerchant(response.data);
      toast.success('Merchant created successfully!');
      fetchMerchants();
      setNewMerchant({
        business_name: '',
        owner_name: '',
        email: '',
        phone: '',
        country: 'Liberia',
        currency_preference: 'Both',
        assigned_port: '',
        password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create merchant');
    } finally {
      setCreateLoading(false);
    }
  };

  // Fetch payment sources for a merchant
  const fetchPaymentSources = async (merchantId) => {
    setPaymentSourcesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/merchants/${merchantId}/payment-sources`, { withCredentials: true });
      setPaymentSources(response.data.payment_sources || []);
    } catch (error) {
      toast.error('Failed to load payment sources');
    } finally {
      setPaymentSourcesLoading(false);
    }
  };

  // Add payment source to merchant
  const handleAddPaymentSource = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/merchants/${selectedMerchant.merchant_id}/payment-sources`, newSource, { withCredentials: true });
      toast.success('Payment source added!');
      setAddSourceOpen(false);
      setNewSource({ network: 'MTN', phone_number: '', label: '' });
      fetchPaymentSources(selectedMerchant.merchant_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment source');
    } finally {
      setActionLoading(false);
    }
  };

  // Open payment sources modal
  const openPaymentSources = (merchant) => {
    setSelectedMerchant(merchant);
    setPaymentSourcesOpen(true);
    fetchPaymentSources(merchant.merchant_id);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const filteredMerchants = merchants.filter(m => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return m.business_name.toLowerCase().includes(query) || m.email.toLowerCase().includes(query);
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div data-testid="admin-merchants-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Merchants</h1>
          <p className="text-sm text-gray-500">Manage merchant accounts on the platform</p>
        </div>
        {isSuperAdmin && (
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200 active:scale-95"
            data-testid="create-merchant-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Merchant
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-colors duration-200 group-focus-within:text-teal-500" />
          <Input
            placeholder="Search merchants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white border-gray-200 h-9 text-sm transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-white border-gray-200 h-9 text-sm transition-all duration-200 hover:border-gray-300">
            <Filter className="w-4 h-4 mr-2 text-gray-400" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          className="h-9 text-sm border-gray-200 transition-all duration-200 hover:border-teal-500 hover:text-teal-600 active:scale-95" 
          onClick={fetchMerchants}
        >
          <RefreshCw className="w-4 h-4 mr-1 transition-transform duration-300 hover:rotate-180" /> Refresh
        </Button>
      </div>

      {/* Merchants List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : filteredMerchants.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100 shadow-sm">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">No merchants found</h3>
          <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-gray-500 font-medium px-4 py-3">Merchant</th>
                <th className="text-left text-gray-500 font-medium px-4 py-3">Status</th>
                <th className="text-left text-gray-500 font-medium px-4 py-3">Transactions</th>
                <th className="text-left text-gray-500 font-medium px-4 py-3">Staff</th>
                <th className="text-left text-gray-500 font-medium px-4 py-3">Created</th>
                <th className="text-right text-gray-500 font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredMerchants.map((merchant, idx) => (
                <tr 
                  key={merchant.merchant_id} 
                  className="hover:bg-gradient-to-r hover:from-transparent hover:via-teal-50/50 hover:to-transparent transition-all duration-300 cursor-pointer"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 group">
                      <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-teal-100">
                        <Building2 className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{merchant.business_name}</p>
                        <p className="text-gray-400 text-xs">{merchant.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {merchant.is_active ? (
                      <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 hover:bg-green-100">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 hover:bg-red-100">
                        <XCircle className="w-3 h-3" /> Suspended
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <ArrowLeftRight className="w-3 h-3 text-gray-400" />
                      {merchant.transaction_count}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Users className="w-3 h-3 text-gray-400" />
                      {merchant.staff_count}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(merchant.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 hover:scale-110 active:scale-95" 
                        onClick={() => openPaymentSources(merchant)}
                        title="Payment Sources"
                      >
                        <Wallet className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-all duration-200 hover:scale-110 active:scale-95" 
                        onClick={() => openDetails(merchant)}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Merchant Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Merchant Details</DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : merchantDetails ? (
            <div className="space-y-5">
              {/* Basic Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-teal-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">{merchantDetails.merchant?.business_name}</h3>
                      <p className="text-gray-500 text-sm">{merchantDetails.merchant?.email}</p>
                    </div>
                  </div>
                  {merchantDetails.merchant?.is_active ? (
                    <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded text-xs font-medium">
                      <XCircle className="w-3 h-3" /> Suspended
                    </span>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400 text-xs">Merchant ID</span>
                    <p className="text-gray-900 font-mono text-xs">{merchantDetails.merchant?.merchant_id}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Phone</span>
                    <p className="text-gray-900">{merchantDetails.merchant?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Created</span>
                    <p className="text-gray-900">{formatDate(merchantDetails.merchant?.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-xs">Total Transactions</span>
                    <p className="text-gray-900">{merchantDetails.stats?.total_transactions || 0}</p>
                  </div>
                </div>
              </div>

              {/* Staff */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Staff Members ({merchantDetails.staff?.length || 0})</h4>
                <div className="space-y-2">
                  {merchantDetails.staff?.map((s) => (
                    <div key={s.staff_id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-gray-900 text-sm font-medium">{s.name}</p>
                        <p className="text-gray-400 text-xs">{s.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        s.role === 'owner' ? 'bg-amber-50 text-amber-600' :
                        s.role === 'manager' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'
                      }`}>{s.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Transactions */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Transactions</h4>
                {merchantDetails.recent_transactions?.length === 0 ? (
                  <p className="text-gray-400 text-sm">No transactions yet</p>
                ) : (
                  <div className="space-y-2">
                    {merchantDetails.recent_transactions?.slice(0, 5).map((tx) => (
                      <div key={tx.transaction_id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-gray-900 text-sm font-mono">{tx.transaction_id}</p>
                          <p className="text-gray-400 text-xs">{tx.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-900 text-sm font-medium">LRD {tx.amount.toLocaleString()}</p>
                          <span className={`text-xs ${
                            tx.status === 'confirmed' ? 'text-green-600' :
                            tx.status === 'pending' ? 'text-amber-600' : 'text-red-600'
                          }`}>{tx.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Support Notes ({merchantDetails.support_notes?.length || 0})</h4>
                  {canAddNotes() && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setNoteModalOpen(true)}>
                      <MessageSquarePlus className="w-3 h-3 mr-1" /> Add Note
                    </Button>
                  )}
                </div>
                {merchantDetails.support_notes?.length === 0 ? (
                  <p className="text-gray-400 text-sm">No support notes</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {merchantDetails.support_notes?.map((note) => (
                      <div key={note.note_id} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-gray-900 text-sm">{note.note}</p>
                        <p className="text-gray-400 text-xs mt-1">{note.created_by_name} • {formatDate(note.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              {isSuperAdmin() && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex gap-3">
                    {merchantDetails.merchant?.is_active ? (
                      <Button variant="destructive" className="flex-1" onClick={() => { setSelectedMerchant(merchantDetails.merchant); setStatusModalOpen(true); }}>
                        <Ban className="w-4 h-4 mr-2" /> Suspend Merchant
                      </Button>
                    ) : (
                      <Button className="flex-1 bg-teal-500 hover:bg-teal-600" onClick={() => handleStatusChange(merchantDetails.merchant, true)} disabled={actionLoading}>
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                        Activate Merchant
                      </Button>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    onClick={() => { setSelectedMerchant(merchantDetails.merchant); setDeleteModalOpen(true); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Merchant Permanently
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add Support Note</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Enter support note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!newNote.trim() || actionLoading} className="bg-teal-500 hover:bg-teal-600">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-600">Suspend Merchant</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 text-sm">
            Are you sure you want to suspend <strong>{selectedMerchant?.business_name}</strong>?
          </p>
          <Textarea
            placeholder="Reason for suspension (optional)..."
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleStatusChange(selectedMerchant, false)} disabled={actionLoading}>
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Merchant Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={(open) => { setDeleteModalOpen(open); setDeleteConfirmText(''); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Merchant Permanently
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm font-medium mb-2">Warning: This action cannot be undone!</p>
              <p className="text-red-700 text-sm">
                This will permanently delete <strong>{selectedMerchant?.business_name}</strong> and all associated data including:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside space-y-1">
                <li>All pending transactions</li>
                <li>Staff members</li>
                <li>Payment sources</li>
                <li>Notifications and audit logs</li>
              </ul>
            </div>
            <div>
              <Label className="text-gray-700">
                Type <strong className="text-red-600">{selectedMerchant?.business_name}</strong> to confirm:
              </Label>
              <Input
                className="mt-2"
                placeholder="Type merchant name to confirm..."
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteModalOpen(false); setDeleteConfirmText(''); }}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteMerchant} 
              disabled={actionLoading || deleteConfirmText !== selectedMerchant?.business_name}
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Merchant Modal */}
      <Dialog open={createModalOpen} onOpenChange={(open) => { setCreateModalOpen(open); setCreatedMerchant(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Merchant</DialogTitle>
          </DialogHeader>
          
          {createdMerchant ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Merchant Created Successfully!
                </div>
                <p className="text-sm text-green-600">
                  {createdMerchant.business_name} has been created.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="font-medium text-gray-900">Merchant Details</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Currency:</span>
                    <span className="text-sm font-medium text-gray-900">{createdMerchant.currency_preference}</span>
                  </div>
                  {createdMerchant.assigned_port && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Assigned Port:</span>
                      <span className="text-sm font-mono bg-teal-100 text-teal-700 px-2 py-1 rounded">Port {createdMerchant.assigned_port}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="font-medium text-gray-900">Login Credentials</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Email:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono bg-white px-2 py-1 rounded border">{createdMerchant.email}</span>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdMerchant.email)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {createdMerchant.temporary_password && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Temp Password:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-white px-2 py-1 rounded border">{createdMerchant.temporary_password}</span>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdMerchant.temporary_password)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {createdMerchant.password_note && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">{createdMerchant.password_note}</p>
                )}
              </div>
              
              <Button className="w-full" onClick={() => { setCreateModalOpen(false); setCreatedMerchant(null); }}>
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreateMerchant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={newMerchant.business_name}
                    onChange={(e) => setNewMerchant({ ...newMerchant, business_name: e.target.value })}
                    placeholder="ABC Boutique"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner Name *</Label>
                  <Input
                    value={newMerchant.owner_name}
                    onChange={(e) => setNewMerchant({ ...newMerchant, owner_name: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    value={newMerchant.email}
                    onChange={(e) => setNewMerchant({ ...newMerchant, email: e.target.value })}
                    placeholder="owner@business.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={newMerchant.phone}
                      onChange={(e) => setNewMerchant({ ...newMerchant, phone: e.target.value })}
                      placeholder="+231 77X XXX XXX"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={newMerchant.country}
                      onChange={(e) => setNewMerchant({ ...newMerchant, country: e.target.value })}
                      placeholder="Liberia"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency Preference</Label>
                  <Select value={newMerchant.currency_preference} onValueChange={(v) => setNewMerchant({ ...newMerchant, currency_preference: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Both">Both (LRD & USD)</SelectItem>
                      <SelectItem value="LRD">LRD Only</SelectItem>
                      <SelectItem value="USD">USD Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assigned Port</Label>
                  <Input
                    type="number"
                    min="1"
                    max="160"
                    value={newMerchant.assigned_port}
                    onChange={(e) => setNewMerchant({ ...newMerchant, assigned_port: e.target.value })}
                    placeholder="1-160"
                  />
                  <p className="text-xs text-gray-400">SIMVault port number (1-160)</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Password (Optional)</Label>
                <Input
                  type="password"
                  value={newMerchant.password}
                  onChange={(e) => setNewMerchant({ ...newMerchant, password: e.target.value })}
                  placeholder="Leave empty for auto-generate"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createLoading} className="bg-teal-600 hover:bg-teal-700">
                  {createLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Create Merchant
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Sources Modal */}
      <Dialog open={paymentSourcesOpen} onOpenChange={setPaymentSourcesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-600" />
              Payment Sources - {selectedMerchant?.business_name}
            </DialogTitle>
          </DialogHeader>
          
          {paymentSourcesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {paymentSources.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Phone className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No payment sources linked yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {paymentSources.map((source) => (
                    <div 
                      key={source.source_id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        source.status === 'active' ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50 opacity-70'
                      }`}
                    >
                      <img 
                        src={source.network === 'MTN' ? MTN_LOGO : ORANGE_LOGO} 
                        alt={source.network} 
                        className="w-10 h-10 rounded object-contain"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{source.phone_number}</div>
                        <div className="text-xs text-gray-500">
                          {source.network} • {source.label || 'No label'}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        source.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {source.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {isSuperAdmin && (
                <Button 
                  onClick={() => setAddSourceOpen(true)} 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment Source
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Payment Source Modal */}
      <Dialog open={addSourceOpen} onOpenChange={setAddSourceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payment Source</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPaymentSource} className="space-y-4">
            <div className="space-y-2">
              <Label>Network</Label>
              <Select value={newSource.network} onValueChange={(v) => setNewSource({ ...newSource, network: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MTN">
                    <div className="flex items-center gap-2">
                      <img src={MTN_LOGO} alt="MTN" className="w-5 h-5 rounded" />
                      MTN Mobile Money
                    </div>
                  </SelectItem>
                  <SelectItem value="Orange">
                    <div className="flex items-center gap-2">
                      <img src={ORANGE_LOGO} alt="Orange" className="w-5 h-5 rounded" />
                      Orange Money
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                value={newSource.phone_number}
                onChange={(e) => setNewSource({ ...newSource, phone_number: e.target.value })}
                placeholder="+231 77X XXX XXX"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Label (Optional)</Label>
              <Input
                value={newSource.label}
                onChange={(e) => setNewSource({ ...newSource, label: e.target.value })}
                placeholder="e.g., Main Business Account"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddSourceOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={actionLoading} className="bg-purple-600 hover:bg-purple-700">
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Add Source
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Merchants;
